import { BoxGeometry, Group, Mesh, Object3D, PlaneGeometry, PointLight, RepeatWrapping } from 'three';
import { ATMOS, EMIT, LIGHTS, SURFACE } from '../render/palette';
import {
  MATERIALS,
  applyContactShading,
  flatVertexColor,
  makeCheckerTexture,
  makeTarmacTexture,
  makeMallTileTexture,
  surface,
} from '../render/materials';
import { fluorescentPanel, storefront } from './props';
import { ceilingGrid, checkoutCounter, floorSafe, stockCabinet, stockShelf, vitrine } from './fixtures';
import { dressStore, schemeFor } from './store-dressing';
import { mergeStatic, type MergeReport } from './merge-static';
import { STORES, item } from '../sim/items';
import type { LockKind, StoreId, Vec3 } from '../sim/types';

/**
 * THE FLOORPLAN FORMAT.
 *
 * Malls are rectilinear, so everything here is axis-aligned. A wall is a line
 * along X or Z; a zone is a rectangle. That constraint is what keeps the plan
 * hand-authorable — `src/world/mall.ts` reads like a description of a building
 * rather than a list of transforms — and it is what lets the builder emit exact
 * box colliders instead of a trimesh of the whole world.
 *
 * The builder produces four things at once from one description:
 *   1. render geometry,
 *   2. physics colliders,
 *   3. the sim's container / drop-off / camera registry,
 *   4. spawn points and fog zones.
 *
 * They cannot drift apart, because there is only one source.
 */

export type Axis = 'x' | 'z';
export type FloorKind = 'tile' | 'tileDark' | 'concrete' | 'asphalt' | 'checker' | 'none';
export type SurfaceKind = 'plaster' | 'plasterDim' | 'concrete' | 'concreteDark' | 'steel' | 'glass';
export type FogZone = keyof typeof ATMOS.density;

export interface BoxCollider {
  id?: string;
  x: number;
  y: number;
  z: number;
  hx: number;
  hy: number;
  hz: number;
  /** Rotation about Y, then about X. Only ramps use pitch. */
  yaw?: number;
  pitch?: number;
  /**
   * Light passes through this; bodies do not.
   *
   * Glazing. A canopy still has to stop a player leaving through the roof, so it
   * needs a collider — but the moon has to reach the atrium floor, because a
   * glass roof over a dark room is the one thing in the building the art
   * direction cannot do without. Only the sky ray honours this; sight lines and
   * interaction deliberately do not, since a guard can see you through a window.
   */
  sky?: boolean;
}

export interface ZoneDef {
  id: string;
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  /** Floor level. */
  y: number;
  floor: FloorKind;
  fog: FogZone;
  /** Build a ceiling at this height. Omit for open-to-above. */
  ceilingY?: number;
  ceiling?: 'slab' | 'grid' | 'glazed';
  /** Tiling repeat for the floor texture, in metres per tile. */
  tileSize?: number;
}

export interface WallDef {
  /** The direction the wall runs. */
  axis: Axis;
  /** Its position on the other axis. */
  at: number;
  from: number;
  to: number;
  /** Base height. Defaults to 0. */
  y?: number;
  height: number;
  thickness?: number;
  surface?: SurfaceKind;
  /** Decorative only — no collider. Soffits, fascia bands, bulkheads overhead. */
  ghost?: boolean;
  /**
   * Collider only — no geometry. Used behind railings and along gallery edges,
   * where something visible already exists but a plaster box would ruin it.
   */
  invisible?: boolean;
}

export interface UnitDef {
  /** Which store trades here. `null` is a vacant unit: dark, shuttered, cover. */
  store: StoreId | null;
  /** Fascia text for a vacant unit. The deadpan-signage slot. */
  label?: string;
  width: number;
  lit?: boolean;
  flicker?: number;
  /** Overrides the store's own lock for the shutter across the entrance. */
  lock?: LockKind;
}

export interface UnitRunDef {
  id: string;
  /** Direction the shopfront line runs. */
  axis: Axis;
  /** Position of the shopfront line on the other axis. */
  at: number;
  /** Where the first unit starts, along `axis`. */
  from: number;
  /** +1 if the shops open toward increasing perpendicular coordinate. */
  facing: 1 | -1;
  y: number;
  height: number;
  /** Interior depth behind the glazing. */
  depth: number;
  /** Pier between adjacent units. */
  gap?: number;
  units: UnitDef[];
  /**
   * The backs of these units face a service corridor, so every unit that is not
   * shuttered gets a door punched through its back wall.
   *
   * Without this the corridors are decoration. Each unit's back cladding sits
   * seven centimetres in front of the shell wall and used to be a solid slab, so
   * the four service doors in the shell opened onto the back of a shop and the
   * whole back-of-house wing connected to the mall only through the food court.
   * A guard leaving the control room walked a hundred and thirty-five metres to
   * reach a point twenty-nine metres away.
   */
  backDoor?: boolean;
}

/** A service door: wide enough for a body and a trolley, low enough to duck under. */
export const BACK_DOOR_W = 1.2;
export const BACK_DOOR_H = 2.2;

/**
 * Where each unit in a run sits along the run's axis.
 *
 * Exported because the shell wall behind a run has to punch its doors at exactly
 * these positions. Hand-typed door coordinates drift silently the moment a unit
 * width changes, and the failure is invisible — the door still looks like a door,
 * it just opens onto a wall.
 */
export function unitCentres(from: number, units: UnitDef[], gap: number): number[] {
  const out: number[] = [];
  let along = from;
  for (const u of units) {
    out.push(along + u.width / 2);
    along += u.width + gap;
  }
  return out;
}

/**
 * A unit gets a back door iff nothing shutters its front. The high-value stores
 * keep exactly one way in, or the tool gating is something you walk around.
 */
export function hasBackDoor(unit: UnitDef): boolean {
  const store = unit.store ? STORES[unit.store] : null;
  const lock = unit.lock ?? store?.lock ?? 'force';
  return !(store && lock !== 'none');
}

export interface PropDef {
  x: number;
  z: number;
  y?: number;
  yaw?: number;
  build: () => Object3D;
  /** Optional collider, in local half-extents around the prop origin. */
  collide?: { hx: number; hy: number; hz: number; cy?: number };
}

export interface StairDef {
  /** Foot of the flight. */
  x: number;
  z: number;
  y: number;
  rise: number;
  run: number;
  width: number;
  /** Yaw in radians. 0 climbs toward +Z. */
  yaw?: number;
  build: (rise: number, run: number, width: number) => Object3D;
}

export interface DropoffPlan {
  id: string;
  kind: 'solo' | 'dock' | 'extraction';
  x: number;
  z: number;
  y?: number;
  yaw?: number;
  radius: number;
  build?: () => Object3D;
}

export interface CameraPlan {
  id: string;
  bank: string;
  x: number;
  z: number;
  y: number;
  /** Compass yaw in radians; 0 looks toward -Z. */
  yaw: number;
  pitch?: number;
  fovDeg?: number;
  rangeM?: number;
}

export interface MallPlan {
  name: string;
  zones: ZoneDef[];
  walls: WallDef[];
  runs: UnitRunDef[];
  props: PropDef[];
  stairs: StairDef[];
  dropoffs: DropoffPlan[];
  cameras: CameraPlan[];
  spawns: { thieves: Vec3[]; guards: Vec3[] };
  patrols: PatrolRoute[];
}

/**
 * A guard's beat: a closed loop of places to stand and look around.
 *
 * Hand-authored rather than generated, for the same reason the mall is. A route
 * is a piece of level design — it decides which shop is watched twice a lap and
 * which one you have ninety seconds alone with — and no graph traversal is going
 * to make that judgement. Consecutive points must be walkable and connected, so
 * the QA harness asserts exactly that.
 */
export interface PatrolRoute {
  id: string;
  /** Human name, used in radio traffic when a guard says where it is. */
  name: string;
  at: Vec3[];
}

/** A container as the world knows it: an id, a lock, and somewhere to stand. */
export interface ContainerPlacement {
  id: string;
  store: StoreId;
  lock: LockKind;
  pos: Vec3;
  /** Where a player must be to interact. */
  reach: number;
  /** The fixture itself, so the game can highlight it and animate it opening. */
  node: Object3D;
  /** Height above `pos` the player should aim at. Vitrines and safes differ. */
  aimY: number;
}

/**
 * A breachable barrier: the shutter across a shop entrance, or a service door.
 * Its collider is removed when the barrier opens, which is the only way the
 * physics world ever changes at runtime.
 */
export interface BarrierPlacement {
  id: string;
  lock: LockKind;
  /** Present while shut; the game removes it on a successful breach. */
  collider: BoxCollider;
  /** Geometry to hide when it opens. */
  node: Object3D;
  pos: Vec3;
  label: string;
}

export interface BuiltMall {
  root: Group;
  colliders: BoxCollider[];
  containers: ContainerPlacement[];
  barriers: BarrierPlacement[];
  dropoffs: Array<{ id: string; kind: 'solo' | 'dock' | 'extraction'; pos: Vec3; radius: number }>;
  cameras: CameraPlan[];
  zones: ZoneDef[];
  /** Kept so the navigation grid can walk the ramps rather than guess at them. */
  stairs: StairDef[];
  spawns: MallPlan['spawns'];
  /** The guards' beats. See `PatrolRoute`. */
  patrols: PatrolRoute[];
  /** What the static-batching pass collapsed. Reported by the QA harness. */
  merged: MergeReport;
}

// ---------------------------------------------------------------------------
// Authoring helpers
// ---------------------------------------------------------------------------

/** A wall run with a doorway punched through it: two jambs plus a lintel. */
export function withDoor(
  wall: WallDef,
  doorAt: number,
  doorWidth = 1.1,
  doorHeight = 2.2,
): WallDef[] {
  const half = doorWidth / 2;
  const out: WallDef[] = [];
  if (doorAt - half > wall.from) out.push({ ...wall, to: doorAt - half });
  if (doorAt + half < wall.to) out.push({ ...wall, from: doorAt + half });
  if (wall.height > doorHeight) {
    out.push({
      ...wall,
      from: doorAt - half,
      to: doorAt + half,
      y: (wall.y ?? 0) + doorHeight,
      height: wall.height - doorHeight,
    });
  }
  return out;
}

/** Four walls around a rectangle. Convenience for rooms. */
export function roomWalls(
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  height: number,
  opts: Partial<WallDef> = {},
): WallDef[] {
  return [
    { axis: 'x', at: z0, from: x0, to: x1, height, ...opts },
    { axis: 'x', at: z1, from: x0, to: x1, height, ...opts },
    { axis: 'z', at: x0, from: z0, to: z1, height, ...opts },
    { axis: 'z', at: x1, from: z0, to: z1, height, ...opts },
  ];
}

const surfaceMat = (kind: SurfaceKind = 'plaster') => {
  switch (kind) {
    case 'plasterDim':
      return MATERIALS.plasterDim();
    case 'concrete':
      return MATERIALS.concrete();
    case 'concreteDark':
      return MATERIALS.concreteDark();
    case 'steel':
      return MATERIALS.steel();
    case 'glass':
      return MATERIALS.glass();
    default:
      return MATERIALS.plaster();
  }
};

let tileTex: ReturnType<typeof makeMallTileTexture> | null = null;
let checkerTex: ReturnType<typeof makeCheckerTexture> | null = null;
let tarmacTex: ReturnType<typeof makeTarmacTexture> | null = null;

function floorMaterial(kind: FloorKind, tileSize = 3) {
  if (kind === 'checker') {
    if (!checkerTex) {
      checkerTex = makeCheckerTexture(1);
      checkerTex.wrapS = checkerTex.wrapT = RepeatWrapping;
    }
    return surface('floorChecker', SURFACE.checkerPale, {
      roughness: 0.86,
      map: checkerTex,
      flat: false,
    });
  }
  if (kind === 'concrete') {
    return surface('floorConcrete', SURFACE.concrete, { roughness: 0.95, flat: false });
  }
  if (kind === 'asphalt') {
    // Outdoor ground. Pale concrete on a fifty-metre plane under full moon and
    // full hemisphere bounce turns into a lightbox that outshines the building
    // it sits in front of — outside has to be the darkest surface in the game.
    // Dark, though, not black: see SURFACE.tarmac for why borrowing `rubber`
    // here quietly ate almost all the light the car park lamps were spending.
    if (!tarmacTex) tarmacTex = makeTarmacTexture(9);
    return surface('floorAsphalt', SURFACE.tarmac, {
      roughness: 0.97,
      map: tarmacTex,
      flat: false,
    });
  }
  if (kind === 'tileDark') {
    return surface('floorTileDark', SURFACE.tileDark, { roughness: 0.3, metalness: 0.2, flat: false });
  }
  if (!tileTex) {
    tileTex = makeMallTileTexture(1);
    tileTex.wrapS = tileTex.wrapT = RepeatWrapping;
  }
  // Low roughness plus a little metalness is what makes the environment map show
  // up on the floor. Without it, polished mall tile reads as flat grey paint.
  void tileSize;
  return surface('floorTile', SURFACE.tileLight, {
    roughness: 0.18,
    metalness: 0.3,
    map: tileTex,
    flat: false,
  });
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

export function buildMall(plan: MallPlan): BuiltMall {
  const root = new Group();
  root.name = plan.name;
  const colliders: BoxCollider[] = [];
  const containers: ContainerPlacement[] = [];
  const barriers: BarrierPlacement[] = [];

  buildZones(plan, root, colliders);
  buildWalls(plan, root, colliders);
  buildRuns(plan, root, colliders, containers, barriers);
  buildStairs(plan, root, colliders);
  buildProps(plan, root, colliders);

  const dropoffs = plan.dropoffs.map((d) => {
    if (d.build) {
      const node = d.build();
      node.position.set(d.x, d.y ?? 0, d.z);
      node.rotation.y = d.yaw ?? 0;
      root.add(node);
    }
    return {
      id: d.id,
      kind: d.kind,
      pos: { x: d.x, y: d.y ?? 0, z: d.z },
      radius: d.radius,
    };
  });

  /**
   * Last, once nothing else will move: collapse the static shell into batches.
   * See `mergeStatic` — this is purely a draw-call optimisation.
   *
   * `?nobatch` turns it off. An optimisation whose effect cannot be measured on
   * demand is an optimisation nobody can defend later, and the merged scene is
   * also the harder one to debug — one batch swallows a thousand objects, so
   * when something looks wrong the first question is always whether it was wrong
   * before the merge too.
   */
  const noBatch =
    typeof location !== 'undefined' && new URLSearchParams(location.search).has('nobatch');
  const merged = noBatch
    ? { before: 0, after: 0, batches: 0, skipped: 0 }
    : mergeStatic(root);

  return {
    root,
    colliders,
    containers,
    barriers,
    dropoffs,
    cameras: plan.cameras,
    zones: plan.zones,
    stairs: plan.stairs,
    spawns: plan.spawns,
    patrols: plan.patrols,
    merged,
  };
}

/**
 * How far apart a floor's vertices are allowed to be, in metres.
 *
 * A floor used to be a plane with four vertices in it, and that one fact was the
 * single largest lighting bug in the building. The lightmapper bakes irradiance
 * *per vertex*, so a light standing over a four-vertex floor deposits everything
 * it has into four corners that may be twenty metres away, and the pool it
 * should have cast simply does not exist. The forecourt was the visible symptom
 * — two downlights recessed into the entrance canopy, pointing straight down at
 * the tarmac, and the tarmac stayed dead black — but it applied to every floor
 * in the game.
 *
 * The lightmapper does subdivide, and this looks at first like its job. It is
 * not, and the reason is worth keeping: `MAX_SUBDIVISIONS` caps that recursion
 * at five levels, which only ever halves the longest edge five times. A slab
 * with a thirty-seven-metre diagonal cannot get near the 1.1m target from there,
 * so the subdivider quietly gave up on exactly the surfaces that needed it most.
 * Raising the cap fixes floors and walls together and was measured as a cliff:
 * 2.84M baked vertices and a 128-second load.
 *
 * So the fix is to hand the subdivider triangles it can finish. At 1.5m the
 * diagonal is 2.1m and two levels get there — and because the subdivider then
 * does the rest, the drawn triangle count lands within 600 of what 1.2m
 * produces, for a visually identical frame (worst shot 0.8% of pixels moved,
 * mean delta 0.09). Anything finer is paid for and thrown away.
 *
 * Cost against no tessellation at all: about 11k triangles and a millisecond,
 * which is inside run-to-run noise on the frame probe.
 */
const FLOOR_GRID = 1.5;
/** Per axis, so one enormous zone cannot blow the bake up on its own. */
const FLOOR_MAX_SEG = 64;

const floorSegments = (metres: number) =>
  Math.max(1, Math.min(FLOOR_MAX_SEG, Math.round(metres / FLOOR_GRID)));

function buildZones(plan: MallPlan, root: Group, colliders: BoxCollider[]) {
  for (const z of plan.zones) {
    const w = z.x1 - z.x0;
    const d = z.z1 - z.z0;
    const cx = (z.x0 + z.x1) / 2;
    const cz = (z.z0 + z.z1) / 2;

    if (z.floor !== 'none') {
      const mat = floorMaterial(z.floor, z.tileSize);
      const geo = flatVertexColor(
        new PlaneGeometry(w, d, floorSegments(w), floorSegments(d)),
      );
      // Per-zone UV repeat so tile size is in metres, not in "whatever the plane is".
      const size = z.tileSize ?? (z.floor === 'checker' ? 1.2 : 3);
      const uv = geo.getAttribute('uv');
      for (let i = 0; i < uv.count; i++) {
        uv.setXY(i, uv.getX(i) * (w / size), uv.getY(i) * (d / size));
      }
      const floor = new Mesh(geo, mat);
      floor.rotation.x = -Math.PI / 2;
      floor.position.set(cx, z.y, cz);
      floor.receiveShadow = true;
      root.add(floor);

      // An upper level needs an underside. A bare plane is invisible from below,
      // so from the atrium floor you would see straight through the gallery to
      // the sky. Give every elevated zone a soffit slab.
      if (z.y > 0.5) {
        const soffit = new Mesh(
          flatVertexColor(new BoxGeometry(w, 0.34, d)),
          MATERIALS.plasterDim(),
        );
        soffit.position.set(cx, z.y - 0.18, cz);
        soffit.receiveShadow = true;
        root.add(soffit);
      }

      // A thin slab collider under every floor. Cheap, and it means a player can
      // never fall through a zone the level author forgot to think about.
      colliders.push({ id: `floor_${z.id}`, x: cx, y: z.y - 0.25, z: cz, hx: w / 2, hy: 0.25, hz: d / 2 });
    }

    if (z.ceilingY !== undefined) {
      if (z.ceiling === 'grid') {
        const grid = ceilingGrid(w, d, 0);
        grid.position.set(cx, z.ceilingY, cz);
        root.add(grid);
      } else if (z.ceiling === 'glazed') {
        // Near-clear glass. What you should read against the sky is the space
        // frame, not the panes — so the structure is deliberately heavier than
        // the glass is dark.
        const glass = new Mesh(new PlaneGeometry(w, d), MATERIALS.canopyGlass());
        glass.rotation.x = Math.PI / 2;
        glass.position.set(cx, z.ceilingY, cz);
        root.add(glass);
        const ribMat = MATERIALS.steelPainted();
        // Transverse ribs — the strongest line in panel 5.
        for (let i = 0; i * 2.5 <= d; i++) {
          const rib = new Mesh(flatVertexColor(new BoxGeometry(w, 0.22, 0.16)), ribMat);
          rib.position.set(cx, z.ceilingY - 0.11, z.z0 + i * 2.5);
          root.add(rib);
        }
        // Longitudinal members. One rib direction reads as a ladder; two read as
        // a roof, and the crossing pattern is what sells the scale of the span.
        for (let i = 0; i * 3.2 <= w; i++) {
          const spar = new Mesh(flatVertexColor(new BoxGeometry(0.14, 0.3, d)), ribMat);
          spar.position.set(z.x0 + i * 3.2, z.ceilingY - 0.28, cz);
          root.add(spar);
        }
      } else {
        const slab = new Mesh(flatVertexColor(new BoxGeometry(w, 0.3, d)), MATERIALS.plasterDim());
        slab.position.set(cx, z.ceilingY + 0.15, cz);
        slab.receiveShadow = true;
        root.add(slab);
      }
      colliders.push({
        id: `ceil_${z.id}`,
        x: cx,
        y: z.ceilingY + 0.3,
        z: cz,
        hx: w / 2,
        hy: 0.2,
        hz: d / 2,
        sky: z.ceiling === 'glazed',
      });
    }
  }
}

function buildWalls(plan: MallPlan, root: Group, colliders: BoxCollider[]) {
  plan.walls.forEach((wall, i) => {
    const len = Math.abs(wall.to - wall.from);
    if (len <= 0.001) return;
    const t = wall.thickness ?? 0.3;
    const y = wall.y ?? 0;
    const mid = (wall.from + wall.to) / 2;
    const w = wall.axis === 'x' ? len : t;
    const d = wall.axis === 'x' ? t : len;
    const cx = wall.axis === 'x' ? mid : wall.at;
    const cz = wall.axis === 'x' ? wall.at : mid;

    if (!wall.invisible) {
      const geo = applyContactShading(new BoxGeometry(w, wall.height, d), {
        floorY: -wall.height / 2,
        falloff: Math.min(2.5, wall.height),
        strength: 0.42,
      });
      const mesh = new Mesh(geo, surfaceMat(wall.surface));
      mesh.position.set(cx, y + wall.height / 2, cz);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      root.add(mesh);
    }

    if (!wall.ghost) {
      colliders.push({
        id: `wall${i}`,
        x: cx,
        y: y + wall.height / 2,
        z: cz,
        hx: w / 2,
        hy: wall.height / 2,
        hz: d / 2,
      });
    }
  });
}

/**
 * How far the visible interior back wall stands in front of a run's nominal
 * back plane.
 *
 * Every shop is backed by TWO walls. The mall shell carries a service-corridor
 * wall along the whole run — 0.3 thick, centred on the back plane, so its inner
 * face lands 0.15 INSIDE the unit — and then each unit builds its own back
 * partition here. On the first pass that partition sat 5cm BEHIND the service
 * wall, which meant two things at once: the per-store wall colour never showed
 * on the back wall, and anything a dresser mounted flush to `-depth` was buried.
 * That is what swallowed the jeweller's backlit niches and the medieval sword
 * rack, and it cost several capture rounds to find because the symptom looks
 * exactly like a lighting bug.
 *
 * So the partition is now clad in FRONT of the service wall, and dressers and
 * container placement are handed the shortened depth. In a dresser's local frame
 * `-depth` is the surface you can actually see and hang things on.
 */
const BACK_CLAD = 0.17;

/**
 * Shop units. Each one gets a shopfront, an interior room with its own floor,
 * ceiling and partitions, a set of containers derived from what the store sells,
 * and — if it is locked — a shutter barrier across the entrance.
 */
function buildRuns(
  plan: MallPlan,
  root: Group,
  colliders: BoxCollider[],
  containers: ContainerPlacement[],
  barriers: BarrierPlacement[],
) {
  for (const run of plan.runs) {
    const gap = run.gap ?? 0.5;
    let along = run.from;

    for (const unit of run.units) {
      const centreAlong = along + unit.width / 2;
      const store = unit.store ? STORES[unit.store] : null;
      const lock = unit.lock ?? store?.lock ?? 'force';
      const lit = unit.lit ?? !!store;
      const entrance = Math.min(2.6, unit.width * 0.42);

      // World transform for this unit. `facing` is the outward normal along the
      // perpendicular axis; the shopfront prop is authored facing +Z.
      const pos = alongToWorld(run.axis, run.at, centreAlong);
      const yaw = runYaw(run.axis, run.facing);

      const sf = storefront({
        width: unit.width,
        height: run.height,
        name: store?.name ?? unit.label ?? 'UNIT AVAILABLE',
        tagline: store?.tagline,
        signColor: signColorFor(unit.store),
        lit,
        flicker: unit.flicker,
        entranceWidth: entrance,
        shutter: 'open',
        interior: false,
      });
      sf.position.set(pos.x, run.y, pos.z);
      sf.rotation.y = yaw;
      root.add(sf);

      // Interior room. Built in the unit's local frame then rotated into place.
      const inner = new Group();
      inner.position.set(pos.x, run.y, pos.z);
      inner.rotation.y = yaw;
      root.add(inner);

      // The unit's shell — wall, floor and how hard the ceiling light is driven.
      // Ten stores with ten different sets of fittings still read as ten identical
      // beige boxes if they all share one wall colour and one floor, so identity
      // lives here rather than in the furniture.
      const scheme = schemeFor(unit.store ?? null);

      // The depth a dresser gets to work with: the plane of the visible back
      // wall, not the run's nominal back. See BACK_CLAD.
      const depth = run.depth - BACK_CLAD;

      const floor = new Mesh(
        flatVertexColor(new PlaneGeometry(unit.width, run.depth)),
        scheme.floor(),
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.set(0, 0.02, -run.depth / 2);
      floor.receiveShadow = true;
      inner.add(floor);

      // Side partitions and back wall, as geometry AND colliders. Without the
      // colliders a player walks straight through into the next shop; without the
      // geometry they see through it.
      const partition = (
        lx: number,
        lz: number,
        w: number,
        d: number,
        y0 = 0,
        h = run.height,
      ) => {
        const m = new Mesh(
          applyContactShading(new BoxGeometry(w, h, d), {
            // Measured from the real floor, not from the piece's own base, so a
            // lintel does not get the dark grounding band a wall gets.
            floorY: -(y0 + h / 2),
            falloff: 2.0,
            strength: 0.45,
          }),
          scheme.wall(),
        );
        m.position.set(lx, y0 + h / 2, lz);
        m.receiveShadow = true;
        inner.add(m);
        pushLocalCollider(colliders, pos, yaw, run.y + y0, lx, lz, w, h, d);
      };
      // Back wall clad in front of the shell's service wall, so this is the
      // surface the player sees and the one that carries the store's colour.
      const backZ = -(depth + 0.1);
      if (run.backDoor && hasBackDoor(unit)) {
        // Split around a doorway on the unit's centre line, which is exactly
        // where the shell wall behind it leaves its own gap.
        const side = (unit.width - BACK_DOOR_W) / 2;
        for (const s of [-1, 1]) {
          partition(s * (BACK_DOOR_W + side) / 2, backZ, side, 0.2);
        }
        partition(0, backZ, BACK_DOOR_W, 0.2, BACK_DOOR_H, run.height - BACK_DOOR_H);
        inner.add(doorFrame(BACK_DOOR_W, BACK_DOOR_H, backZ));
      } else {
        partition(0, backZ, unit.width, 0.2);
      }
      partition(-unit.width / 2, -run.depth / 2, 0.2, run.depth);
      partition(unit.width / 2, -run.depth / 2, 0.2, run.depth);

      // Suspended tile ceiling with the odd missing panel — panel 4's whole mood,
      // and the reason a shop reads as a room rather than an open-topped pen.
      const lid = ceilingGrid(unit.width - 0.3, run.depth - 0.3, 0);
      lid.position.set(0, run.height - 0.35, -run.depth / 2);
      inner.add(lid);

      if (lit && scheme.fill > 0.05) {
        const rows = Math.max(1, Math.round(unit.width / 4.5));
        for (let i = 0; i < rows; i++) {
          const panel = fluorescentPanel(0.9, run.depth * 0.55, {
            ceilingY: run.height - 0.42,
            // Scaled by the scheme too, not just the point light below. A dim
            // jeweller with four blazing ceiling strips in it is not dim.
            intensity: (store ? 1 : 0.5) * scheme.fill,
          });
          panel.position.set(
            -unit.width / 2 + (unit.width / (rows + 1)) * (i + 1),
            0,
            -run.depth * 0.5,
          );
          inner.add(panel);
        }

        // One real point light per lit unit. The ceiling panels above are
        // emissive quads — they glow but they illuminate nothing, so without
        // this a trading store is a black hole behind a lit sign, which is
        // exactly what the first screenshot pass showed. One light per UNIT,
        // not per fitting: twelve lit units is an affordable shader budget,
        // thirty is not.
        // Pushed deep into the unit and given a distance that dies before the
        // shopfront. At 16 intensity sitting at mid-depth it flooded back out
        // through the glazing and washed the fascia, the mall ceiling and the
        // concourse in the same warm tan — a dozen units doing that at once is
        // what turned the whole building milky. The interior should glow; the
        // spill onto the walkway should be a puddle at the threshold, no more.
        const fill = new PointLight(
          scheme.fillColor ?? (store ? LIGHTS.shop.color : LIGHTS.fluoro.color),
          (store ? 9 : 6) * scheme.fill,
          run.depth * 1.5,
          1.9,
        );
        fill.position.set(0, run.height - 1.2, -run.depth * 0.68);
        inner.add(fill);
      }

      // Glazing either side of the entrance blocks movement; the entrance does not.
      const sideW = (unit.width - entrance) / 2;
      if (sideW > 0.05) {
        for (const s of [-1, 1]) {
          pushLocalCollider(
            colliders,
            pos,
            yaw,
            run.y,
            s * (entrance / 2 + sideW / 2),
            0,
            sideW,
            run.height,
            0.16,
          );
        }
      }

      if (store && lock !== 'none') {
        const shutter = buildShutter(unit.width, run.height, entrance);
        shutter.position.set(0, 0, 0.14);
        inner.add(shutter);
        const world = localToWorld(pos, yaw, 0, 0);
        barriers.push({
          id: `shutter_${unit.store}`,
          lock,
          collider: {
            id: `shutter_${unit.store}`,
            x: world.x,
            y: run.y + run.height / 2,
            z: world.z,
            hx: entrance / 2,
            hy: run.height / 2,
            hz: 0.12,
            yaw,
          },
          node: shutter,
          pos: { x: world.x, y: run.y + 1.2, z: world.z },
          label: `${store.name} — ${lock}`,
        });
      }

      if (store) {
        placeContainers(unit.store!, store.lock, inner, pos, yaw, run.y, unit.width, depth, containers);
        // Signature fittings. Kept separate from the containers above because
        // these are scenery only — no lock, no reach, nothing the sim can see.
        inner.add(dressStore(unit.store!, unit.width, depth, run.height));
      }

      along += unit.width + gap;
    }
  }
}

/** A roller shutter across just the entrance gap. Hidden when breached. */
function buildShutter(unitWidth: number, height: number, entrance: number): Group {
  const g = new Group();
  g.name = 'shutter';
  void unitWidth;
  const slats = Math.max(2, Math.round((height - 0.9) / 0.16));
  for (let i = 0; i < slats; i++) {
    const s = new Mesh(
      flatVertexColor(new BoxGeometry(entrance, 0.14, 0.05)),
      MATERIALS.steelDark(),
    );
    s.position.set(0, height - 0.9 - i * 0.16 - 0.07, 0);
    s.castShadow = true;
    g.add(s);
  }
  return g;
}

/**
 * Which containers a store gets, derived from what it sells rather than authored
 * per unit. A store with a five-figure item earns a safe; a store behind a cut
 * lock earns a vitrine. The sim then deals the best loot into the hardest
 * container, so "where is the good stuff" is answerable by looking at the room.
 */
function placeContainers(
  storeId: StoreId,
  storeLock: LockKind,
  inner: Group,
  pos: { x: number; z: number },
  yaw: number,
  baseY: number,
  width: number,
  depth: number,
  out: ContainerPlacement[],
) {
  const store = STORES[storeId];
  const best = Math.max(...store.spawns.map((s) => item(s.item).value));
  const add = (
    node: Object3D,
    lx: number,
    lz: number,
    lock: LockKind,
    reach = 1.6,
    aimY = 1.0,
  ) => {
    node.position.set(lx, 0, lz);
    inner.add(node);
    const world = localToWorld(pos, yaw, lx, lz);
    out.push({
      id: `${storeId}_${out.filter((c) => c.store === storeId).length}`,
      store: storeId,
      lock,
      pos: { x: world.x, y: baseY, z: world.z },
      reach,
      node,
      aimY,
    });
  };

  // Two open shelf runs against the back wall. Always robbable — a thief with no
  // tools should still have something to do in every store they can enter.
  const shelfW = Math.min(2.0, width * 0.42);
  add(stockShelf(shelfW), -width * 0.25, -depth + 0.45, 'none', 1.7, 1.1);
  add(stockShelf(shelfW), width * 0.25, -depth + 0.45, 'none', 1.7, 1.1);

  if (storeLock === 'cut') {
    add(vitrine('cut', Math.min(1.7, width * 0.4)), 0, -depth * 0.45, 'cut', 1.5, 0.95);
  } else if (storeLock === 'pick' || storeLock === 'card') {
    const cab = stockCabinet(storeLock);
    cab.rotation.y = Math.PI;
    add(cab, -width * 0.3, -depth + 0.35, storeLock, 1.5, 1.0);
  } else if (storeLock === 'force') {
    add(stockCabinet('force'), width * 0.28, -depth * 0.5, 'force', 1.5, 1.0);
  }

  // Out on the shop floor rather than against the back wall. Tucked into the
  // corner it was 20cm inside the right-hand shelf run, which meant the
  // twenty-two-second drill job — the biggest single decision in the game — had
  // no line of sight from anywhere and could not be started at all.
  if (best >= 7000) {
    add(floorSafe(), width * 0.42, -depth * 0.32, 'safe', 1.5, 0.35);
  }

  const counter = checkoutCounter(Math.min(2.4, width * 0.5));
  counter.position.set(-width * 0.22, 0, -1.35);
  counter.rotation.y = Math.PI;
  inner.add(counter);
}

function buildStairs(plan: MallPlan, root: Group, colliders: BoxCollider[]) {
  plan.stairs.forEach((s, i) => {
    const node = s.build(s.rise, s.run, s.width);
    node.position.set(s.x, s.y, s.z);
    node.rotation.y = s.yaw ?? 0;
    root.add(node);

    // One pitched slab collider instead of per-step boxes: the character
    // controller slides up a ramp far more reliably than it climbs stairs, and
    // the visual steps still read correctly.
    const len = Math.hypot(s.rise, s.run);
    const pitch = -Math.atan2(s.rise, s.run);
    const yaw = s.yaw ?? 0;
    const midAlong = s.run / 2;
    const midY = s.y + s.rise / 2;
    colliders.push({
      id: `stair${i}`,
      x: s.x + Math.sin(yaw) * midAlong,
      y: midY,
      z: s.z + Math.cos(yaw) * midAlong,
      hx: s.width / 2,
      hy: 0.12,
      hz: len / 2,
      yaw,
      pitch,
    });
  });
}

function buildProps(plan: MallPlan, root: Group, colliders: BoxCollider[]) {
  plan.props.forEach((p, i) => {
    const node = p.build();
    node.position.set(p.x, p.y ?? 0, p.z);
    node.rotation.y = p.yaw ?? 0;
    root.add(node);
    if (p.collide) {
      colliders.push({
        id: `prop${i}`,
        x: p.x,
        y: (p.y ?? 0) + (p.collide.cy ?? p.collide.hy),
        z: p.z,
        hx: p.collide.hx,
        hy: p.collide.hy,
        hz: p.collide.hz,
        yaw: p.yaw ?? 0,
      });
    }
  });
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function alongToWorld(axis: Axis, at: number, along: number) {
  return axis === 'x' ? { x: along, z: at } : { x: at, z: along };
}

/**
 * Yaw that points a +Z-facing prop outward from a shopfront line.
 * A run along X faces ±Z; a run along Z faces ±X.
 */
function runYaw(axis: Axis, facing: 1 | -1): number {
  if (axis === 'x') return facing === 1 ? 0 : Math.PI;
  return facing === 1 ? Math.PI / 2 : -Math.PI / 2;
}

function localToWorld(pos: { x: number; z: number }, yaw: number, lx: number, lz: number) {
  const s = Math.sin(yaw);
  const c = Math.cos(yaw);
  return { x: pos.x + lx * c + lz * s, z: pos.z - lx * s + lz * c };
}

/**
 * The jamb around a service door, in a unit's local frame.
 *
 * Purely cosmetic and worth the triangles: an unframed 1.2m hole in a plaster
 * wall reads as a hole somebody forgot to fill, a framed one reads as a door
 * staff use every day.
 */
function doorFrame(w: number, h: number, lz: number): Object3D {
  const g = new Group();
  const T = 0.08;
  for (const s of [-1, 1]) {
    const jamb = new Mesh(flatVertexColor(new BoxGeometry(T, h + T, 0.32)), MATERIALS.steel());
    jamb.position.set(s * (w / 2 + T / 2), (h + T) / 2, lz);
    g.add(jamb);
  }
  const head = new Mesh(flatVertexColor(new BoxGeometry(w + T * 2, T, 0.32)), MATERIALS.steel());
  head.position.set(0, h + T / 2, lz);
  g.add(head);
  return g;
}

function pushLocalCollider(
  out: BoxCollider[],
  pos: { x: number; z: number },
  yaw: number,
  baseY: number,
  lx: number,
  lz: number,
  w: number,
  h: number,
  d: number,
) {
  const world = localToWorld(pos, yaw, lx, lz);
  out.push({ x: world.x, y: baseY + h / 2, z: world.z, hx: w / 2, hy: h / 2, hz: d / 2, yaw });
}

function signColorFor(store: StoreId | null) {
  if (!store) return EMIT.signAmber.clone().multiplyScalar(0.5);
  const lock = STORES[store]?.lock ?? 'none';
  // The fascia colour matches the store's lock, so a thief can plan a route from
  // the far end of the atrium by reading nothing but the signs.
  switch (lock) {
    case 'cut':
      return EMIT.signMagenta;
    case 'pick':
      return EMIT.signCyan;
    case 'card':
      return EMIT.lcd;
    case 'force':
      return EMIT.signAmber;
    default:
      return EMIT.shopWarm;
  }
}

/** Which zone a world position sits in, for fog. Innermost (smallest) wins. */
export function zoneAt(zones: ZoneDef[], x: number, y: number, z: number): ZoneDef | null {
  let best: ZoneDef | null = null;
  let bestArea = Infinity;
  for (const zn of zones) {
    if (x < zn.x0 || x > zn.x1 || z < zn.z0 || z > zn.z1) continue;
    if (y < zn.y - 1.5 || y > zn.y + (zn.ceilingY !== undefined ? zn.ceilingY + 2 : 12)) continue;
    const area = (zn.x1 - zn.x0) * (zn.z1 - zn.z0);
    if (area < bestArea) {
      best = zn;
      bestArea = area;
    }
  }
  return best;
}
